import { supabase } from '../../config/supabase';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserById, updateUser, deleteUser, uploadAvatar, removeAvatar, formatPhoneDisplay } from '../../lib/supabase/users';
import {
    FiUser, FiTruck, FiAlertCircle, FiSettings,
    FiArrowLeft, FiEdit2, FiSave, FiX, FiTrash2, FiClock,
    FiBriefcase, FiCheck, FiCamera, FiUpload, FiLogOut
} from 'react-icons/fi';
import { MdEditNotifications, MdOutlineEditNotifications, MdVerified, MdWarning } from 'react-icons/md';
import ConfirmationPopup from '../popup/ConfirmationPopup';
import { hasPermission } from '../../lib/permissions';
import DropdownSection from '../common/DropdownSection';

// ─── Role colours (matches Users.js) ─────────────────────────────────────────

const ROLE_COLOR = {
    super_admin: 'bg-purple-100 text-purple-700',
    admin: 'bg-blue-100   text-blue-700',
    designer: 'bg-emerald-100 text-emerald-700',
    estimator: 'bg-amber-100  text-amber-700',
    driver: 'bg-rose-100   text-rose-700',
};
const ROLE_AVATAR = {
    super_admin: 'bg-purple-100 text-purple-700',
    admin: 'bg-blue-100   text-blue-700',
    designer: 'bg-emerald-100 text-emerald-700',
    estimator: 'bg-amber-100  text-amber-700',
    driver: 'bg-rose-100   text-rose-700',
};
const ROLE_LABEL = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    designer: 'Designer',
    estimator: 'Estimator',
    driver: 'Driver',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Clickable avatar — shows existing photo with a camera-overlay on hover,
 * or a dashed upload zone when no avatar exists yet.
 */
const AvatarUpload = ({ user, uploading, onFileSelected, onRemove, canEdit }) => {
    const inputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const initial = (user.full_name || user.username || '?')[0].toUpperCase();
    const avatarBg = ROLE_AVATAR[user.role] || 'bg-gray-100 text-gray-700';

    // Once upload finishes (uploading flips back to false), discard the local
    // blob URL — the real avatar_url from the server takes over.
    useEffect(() => {
        if (!uploading && previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    }, [uploading]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClick = () => {
        if (canEdit && !uploading) inputRef.current?.click();
    };

    const handleChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Show a local blob preview immediately — no waiting for the upload
            const localUrl = URL.createObjectURL(file);
            setPreviewUrl(localUrl);
            onFileSelected(file);
        }
        e.target.value = '';
    };

    // While uploading use the local blob; otherwise fall back to stored URL
    const displayUrl = previewUrl || user.avatar_url;

    return (
        <div className="relative flex-shrink-0">
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleChange}
            />

            {displayUrl ? (
                /* ── Has avatar (or local preview) ──────────────────────── */
                <div className="relative">
                    <button
                        type="button"
                        onClick={handleClick}
                        disabled={!canEdit || uploading}
                        className="relative w-16 h-16 rounded-full overflow-hidden ring-2 ring-white shadow-md group focus:outline-none"
                        title={canEdit ? 'Change photo' : undefined}
                    >
                        <img
                            src={displayUrl}
                            alt={user.full_name || user.username}
                            className="w-full h-full object-cover"
                        />
                        {canEdit && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                                {uploading
                                    ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    : <FiCamera className="w-5 h-5 text-white" />
                                }
                            </div>
                        )}
                    </button>
                    {/* Remove badge — only when there's a real saved avatar, not a blob preview */}
                    {canEdit && !uploading && user.avatar_url && (
                        <button
                            type="button"
                            onClick={onRemove}
                            className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-colors z-10"
                            title="Remove photo"
                        >
                            <FiX className="w-3 h-3" />
                        </button>
                    )}
                </div>
            ) : (
                /* ── No avatar yet ──────────────────────────────────────── */
                <button
                    type="button"
                    onClick={handleClick}
                    disabled={!canEdit || uploading}
                    className={`w-16 h-16 rounded-full flex flex-col items-center justify-center gap-1 ring-2 ring-white shadow-md transition-all focus:outline-none ${canEdit
                        ? `${avatarBg} border-2 border-dashed border-current opacity-80 hover:opacity-100`
                        : `${avatarBg}`
                        }`}
                    title={canEdit ? 'Upload photo' : undefined}
                >
                    {uploading ? (
                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : canEdit ? (
                        <>
                            <FiUpload className="w-4 h-4" />
                            <span className="text-[9px] font-semibold leading-none">PHOTO</span>
                        </>
                    ) : (
                        <span className="text-lg font-bold">{initial}</span>
                    )}
                </button>
            )}
        </div>
    );
};

const Section = ({ title, icon: Icon, section, editingSection, saving, canEdit, onStartEdit, onCancel, onSave, children }) => {
    const isEditing = editingSection === section;
    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700">{title}</span>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={onSave}
                                    disabled={saving}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white rounded-lg text-xs font-semibold transition-colors"
                                >
                                    <FiSave className="w-3.5 h-3.5" />
                                    {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                    onClick={onCancel}
                                    disabled={saving}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <FiX className="w-3.5 h-3.5" />
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={onStartEdit}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-100 text-gray-600 border border-gray-200 rounded-lg text-xs font-medium transition-colors"
                            >
                                <FiEdit2 className="w-3.5 h-3.5" />
                                Edit
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="divide-y divide-gray-100">{children}</div>
        </div>
    );
};

const Field = ({ label, value, field, type = 'text', options, editData, setEditData, isEditing, canEditField = false, render, hint }) => (
    <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-3 gap-1.5 sm:gap-4">
            <span className="text-xs font-medium text-gray-500 flex-shrink-0 sm:w-32">
                {label}
            </span>

            {(isEditing || canEditField) ? (
                type === 'select' ? (
                    <select
                        value={editData[field] ?? ''}
                        onChange={e => setEditData({ ...editData, [field]: e.target.value })}
                        className="w-full sm:flex-1 px-3 py-2 sm:py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                ) : (
                    <input
                        type={type}
                        value={editData[field] ?? ''}
                        onChange={e => setEditData({ ...editData, [field]: e.target.value })}
                        className="w-full sm:flex-1 px-3 py-2 sm:py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                )
            ) : (
                <span className="text-sm text-gray-900 font-medium sm:text-right break-words min-w-0">
                    {render
                        ? render(value)
                        : (value || <span className="text-gray-400 font-normal">Not set</span>)}
                </span>
            )}
        </div>

        {hint && <div className="px-3 sm:px-4 pb-3 -mt-1 sm:-mt-2">{hint}</div>}
    </div>
);

const MetaChip = ({ label, value }) => (
    <div className="flex flex-col gap-0.5 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100">
        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{label}</span>
        <span className="text-xs font-semibold text-gray-700">{value}</span>
    </div>
);

const PERMISSION_GROUPS = [
    {
        title: 'Page Access',
        description: 'Controls which pages and tools the user can see.',
        permissions: [
            {
                key: 'SEEPAGE_MYJOBS',
                label: 'Assigned Jobs Page',
                description: 'Allow user to see assigned jobs page.',
            },
        ],
    },
    {
        title: 'Invoice & Financial',
        description: 'Controls invoice review and financial tracking features.',
        permissions: [
            {
                key: 'GET_WEEKLY_INV_RECORDS',
                label: 'Weekly Invoice Review Popup',
                description: 'Allow user to receive and confirm weekly invoice review popup records.',
            },
        ],
    },
    {
        title: 'Assignment Permissions',
        description: 'Controls who the user can assign jobs to.',
        permissions: [
            {
                key: 'ASSIGN_DESIGNERS',
                label: 'Assign Designers',
                description: 'Allows user to assign designers.',
            },
            {
                key: 'ASSIGN_ESTIMATOR',
                label: 'Assign Estimators',
                description: 'Allows user to assign estimators.',
            },
            {
                key: 'ASSIGN_DESIGNER_SELF',
                label: 'Assign Design To Self',
                description: 'Allows user to assign design department jobs to self.',
            },
            {
                key: 'ASSIGN_ESTIMATOR_SELF',
                label: 'Assign Estimation To Self',
                description: 'Allows user to assign estimation department jobs to self.',
            },
        ],
    },
    {
        title: 'Integrations',
        description: 'Controls integration connection access.',
        permissions: [
            {
                key: 'CONNECT_INTEGRATIONS',
                label: 'Connect Integrations',
                description: 'Allows user to access integration connection settings.',
            },
            {
                key: 'CONNECT_INTEGRATION_XERO',
                label: 'Connect Xero',
                description: 'Allows user to connect Xero integration.',
            },
        ],
    },
    {
        title: 'Email & Customer Communication',
        description: 'Controls email sending tools.',
        permissions: [
            {
                key: 'SEND_FILES_EMAIL',
                label: 'Send Files & Booked Dates Email',
                description: 'Allows user to send booked dates mail or send files directly to customer via Notify.',
            },
        ],
    },
    {
        title: 'Estimation Notifications',
        description: 'Controls notifications when an estimator completes a job.',
        permissions: [
            {
                key: 'GET_ESTIMATION_NOTIFICATIONS_MAIL',
                label: 'Estimator Completed Email',
                description: 'Get notification by email when estimator completes a job.',
            },
            {
                key: 'GET_ESTIMATION_NOTIFICATIONS_PUSH',
                label: 'Estimator Completed Push',
                description: 'Get notification on mobile device when estimator completes a job.',
            },
        ],
    },
    {
        title: 'User Management',
        description: 'High level account management permissions.',
        permissions: [
            {
                key: 'MODIFY_USER_ROLES',
                label: 'Modify User Roles',
                description: 'Allows modifying user roles. Super admin permissions cannot be removed from super admins.',
                superAdminOnly: true,
            },
        ],
    },
];


const PermissionToggle = ({ permission, enabled, disabled, saving, onToggle }) => {
    return (
        <button
            type="button"
            disabled={disabled || saving}
            onClick={onToggle}
            className={`w-full flex items-center justify-between gap-3 p-3 rounded-xl border transition-all text-left ${enabled
                ? 'bg-teal-50 border-teal-200'
                : 'bg-white border-gray-200 hover:bg-gray-50'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
            <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800">
                    {permission.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 leading-5">
                    {permission.description}
                </p>
            </div>

            <span
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-teal-600' : 'bg-gray-300'
                    }`}
            >
                <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                />
            </span>
        </button>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const UserDetails = ({ modalUserId, onClose } = {}) => {
    const { userId: paramsUserId, companySlug } = useParams();
    const navigate = useNavigate();
    const { userProfile, signOut } = useAuth();


    const isModal = !!onClose;
    const userId = modalUserId ?? paramsUserId;

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingSection, setEditingSection] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [removingAvatar, setRemovingAvatar] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState('');


    const [showAccountDeletePopup, setShowAccountDeletePopup] = useState(false);
    const [deleteStep, setDeleteStep] = useState(1);
    const [deleteText, setDeleteText] = useState('');
    const [deleteCode, setDeleteCode] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [savingPermission, setSavingPermission] = useState(null);

    useEffect(() => {
        if (userId) fetchUser();
    }, [userId]);

    useEffect(() => {
        if (user?.full_name) {
            setNameValue(user.full_name);
        }
    }, [user]);

    const fetchUser = async () => {
        try {
            setLoading(true);
            const data = await getUserById(userId);
            setUser(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (section) => {
        setEditingSection(section);
        setEditData({ ...user });
    };

    const cancelEditing = () => {
        setEditingSection(null);
        setEditData({});
    };

    const saveEdit = async () => {
        try {
            setSaving(true);
            const { user: updated } = await updateUser(userId, editData);
            setUser(updated);
            setEditingSection(null);
            setEditData({});
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleNameSave = async () => {
        if (!nameValue.trim() || nameValue === user.full_name) {
            setEditingName(false);
            return;
        }

        try {
            setSaving(true);
            const { user: updated } = await updateUser(userId, {
                full_name: nameValue.trim()
            });
            setUser(updated);
            setEditingName(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async () => {
        try {
            await deleteUser(userId);
            if (isModal) {
                onClose(true);
            } else {
                navigate(`/${companySlug}/manage-users`);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleRemoveAvatar = async () => {
        try {
            setRemovingAvatar(true);
            setError(null);
            const { user: updated } = await removeAvatar(userId, user.avatar_url);
            setUser(updated);
        } catch (err) {
            setError('Failed to remove avatar: ' + err.message);
        } finally {
            setRemovingAvatar(false);
        }
    };

    const handleAvatarUpload = async (file) => {
        try {
            setUploadingAvatar(true);
            setError(null);
            const { user: updated } = await uploadAvatar(userId, file);
            setUser(updated);
        } catch (err) {
            setError('Failed to upload avatar: ' + err.message);
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleLogout = async () => {
        try {
            // Sign out
            const { error } = await signOut();

            if (error) {
                console.error('Logout error:', error);
                return;
            }

            // Use window.location for a full page reload to ensure clean state
            // This is especially important for iOS Safari which can cache state
            window.location.href = '/login';
        } catch (error) {
            console.error('Logout error:', error);
            // Fallback to window.location even on error
            window.location.href = '/login';
        }
    };

    const handleRequestDeletionCode = async () => {
        try {
            setDeleteLoading(true);
            setError(null);

            const { data: { session } } = await supabase.auth.getSession();

            const res = await fetch(
                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/request-account-deletion-code`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ confirmationText: deleteText }),
                }
            );

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Failed to send verification code');

            setDeleteStep(2);
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleConfirmAccountDeletion = async () => {
        try {
            setDeleteLoading(true);
            setError(null);

            const { data: { session } } = await supabase.auth.getSession();

            const res = await fetch(
                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/confirm-account-deletion`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ code: deleteCode }),
                }
            );

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || 'Failed to delete account');

            await signOut();
            window.location.href = '/login?accountDeleted=true';
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handlePermissionToggle = async (permissionKey) => {
        if (!user?.id || savingPermission) return;

        const currentPermissions = user.permissions || [];
        const hasCurrentPermission = currentPermissions.includes(permissionKey);

        if (
            permissionKey === 'MODIFY_USER_ROLES' &&
            user.role === 'super_admin'
        ) {
            setError('MODIFY_USER_ROLES cannot be changed for super admins.');
            return;
        }

        if (
            permissionKey === 'MODIFY_USER_ROLES' &&
            user.id === userProfile?.id &&
            hasCurrentPermission
        ) {
            setError('You cannot remove role management permission from yourself.');
            return;
        }

        const nextPermissions = hasCurrentPermission
            ? currentPermissions.filter((p) => p !== permissionKey)
            : [...currentPermissions, permissionKey];

        try {
            setSavingPermission(permissionKey);
            setError(null);

            const { user: updated } = await updateUser(user.id, {
                permissions: nextPermissions,
            });

            setUser(updated);
        } catch (err) {
            setError(err.message || 'Failed to update permission.');
        } finally {
            setSavingPermission(null);
        }
    };


    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Not set';
    const fmtDT = (d) => d ? new Date(d).toLocaleString('en-AU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';

    const isSelf = userProfile?.id === user?.id;

    const canEdit = hasPermission(userProfile?.role, ['super_admin', 'admin']);
    const canEditSelfBasic = isSelf;
    const canDelete = userProfile?.role === 'super_admin' && user?.role !== 'super_admin' && user?.id !== userProfile?.id;


    const currentUserPermissions = userProfile?.permissions || [];

    const canModifyUserPermissions =
        userProfile?.role === 'super_admin' ||
        currentUserPermissions.includes('MODIFY_USER_PERMISSIONS');

    const canModifyUserRoles =
        userProfile?.role === 'super_admin' ||
        currentUserPermissions.includes('MODIFY_USER_ROLES');

    const showPermissionSection = canModifyUserPermissions || canModifyUserRoles;

    const canDeleteOwnAccount = isSelf && !user?.is_deleted;

    // ── Inner content ─────────────────────────────────────────────────────────

    const content = (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={isModal ? onClose : () => navigate(`/${companySlug}/manage-users`)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    {isModal ? <FiX className="w-5 h-5" /> : <FiArrowLeft className="w-5 h-5" />}
                </button>
                <span className="text-sm font-semibold text-gray-700">User Details</span>
                <div className="w-8" />
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-3 sm:space-y-4">

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                        <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
                    </div>
                ) : user ? (
                    <>
                        {/* Profile hero */}
                        <div className="flex items-start sm:items-center gap-3 sm:gap-4 bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-4">
                            <AvatarUpload
                                user={user}
                                uploading={uploadingAvatar || removingAvatar}
                                onFileSelected={handleAvatarUpload}
                                onRemove={handleRemoveAvatar}
                                canEdit={canEdit || canEditSelfBasic}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {editingName ? (
                                            <>
                                                <input
                                                    value={nameValue}
                                                    onChange={(e) => setNameValue(e.target.value)}
                                                    className="w-full max-w-[190px] sm:max-w-none px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                                                    autoFocus
                                                />

                                                <button
                                                    onClick={handleNameSave}
                                                    disabled={saving}
                                                    className="p-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-md"
                                                >
                                                    <FiCheck className="w-4 h-4" />
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setEditingName(false);
                                                        setNameValue(user.full_name || '');
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleNameSave();
                                                        if (e.key === 'Escape') {
                                                            setEditingName(false);
                                                            setNameValue(user.full_name || '');
                                                        }
                                                    }}
                                                    className="p-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md"
                                                >
                                                    <FiX className="w-4 h-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="font-bold text-gray-900 text-base leading-tight">
                                                    {user.full_name || user.username}
                                                </span>

                                                {isSelf && (
                                                    <button
                                                        onClick={() => setEditingName(true)}
                                                        className="p-1 text-gray-400 hover:text-gray-600"
                                                        title="Edit name"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {user.role === 'super_admin' && (
                                        <MdVerified className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                                    )}
                                </div>
                                <span className="text-sm text-gray-400">@{user.username}</span>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${ROLE_COLOR[user.role] || 'bg-gray-100 text-gray-600'}`}>
                                        {ROLE_LABEL[user.role] || user.role}
                                    </span>
                                    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                        {user.status === 'active' ? (
                                            <><FiCheck className="w-3 h-3 mr-1" />Active</>
                                        ) : 'Inactive'}
                                    </span>
                                    {user.position && (
                                        <span className="inline-flex items-center text-xs font-medium px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                            {user.position}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <DropdownSection
                            title="User Information"
                            titleStyle="text-zinc-700 text-sm"
                            icon={FiUser}
                            iconStyle="text-zinc-800 !w-4 !h-4 self-center"
                            subtext={``}
                            subtextStyle="text-xs "
                            defaultOpen={false}
                        >
                            {/* Basic Information */}
                            <Section
                                title="Basic Information" icon={FiUser} section="basic"
                                editingSection={editingSection} saving={saving} canEdit={canEdit}
                                onStartEdit={() => startEditing('basic')}
                                onCancel={cancelEditing} onSave={saveEdit}
                            >
                                <Field label="Full Name" value={user.full_name} field="full_name"
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'} />
                                <Field label="Username" value={user.username} field="username"
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'} />
                                <Field label="Email" value={user.email} field="email" type="email"
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'}
                                    render={v => <a href={`mailto:${v}`} className="text-blue-600 hover:underline">{v}</a>} />
                                <Field label="Phone" value={user.phone ? `+61 ${formatPhoneDisplay(user.phone)}` : null} field="phone" type="tel"
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'} />
                            </Section>

                            {/* Employment */}
                            <Section
                                title="Employment" icon={FiBriefcase} section="employment"
                                editingSection={editingSection} saving={saving} canEdit={canEdit}
                                onStartEdit={() => startEditing('employment')}
                                onCancel={cancelEditing} onSave={saveEdit}
                            >
                                <Field label="Position" value={user.position} field="position"
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'employment'} />
                                {user.role === 'designer' && (
                                    <Field
                                        label="Department"
                                        value={user.department ? user.department.charAt(0).toUpperCase() + user.department.slice(1) : null}
                                        field="department"
                                        type="select"
                                        options={[
                                            { value: '', label: 'None' },
                                            { value: 'roof', label: 'Roof' },
                                            { value: 'wall', label: 'Wall' },
                                            { value: 'floor', label: 'Floor' },
                                        ]}
                                        editData={editData}
                                        setEditData={setEditData}
                                        isEditing={editingSection === 'employment'}
                                        hint={
                                            (editingSection === 'employment' && !editData.department) ||
                                                (editingSection !== 'employment' && !user.department)
                                                ? <p className="text-xs text-amber-600 flex items-center gap-1"><FiAlertCircle className="w-3 h-3 flex-shrink-0" /> If no department is selected, this designer can be assigned to or complete any department.</p>
                                                : null
                                        }
                                    />
                                )}
                                <Field label="Role" value={ROLE_LABEL[user.role] || user.role} field="role" type="select"
                                    options={[
                                        //    { value: 'super_admin', label: 'Super Admin' },
                                        { value: 'admin', label: 'Admin' },
                                        { value: 'designer', label: 'Designer' },
                                        { value: 'estimator', label: 'Estimator' },
                                        { value: 'driver', label: 'Driver' },
                                    ]}
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'employment'} />
                                <Field label="Status" value={user.status === 'active' ? 'Active' : 'Inactive'} field="status" type="select"
                                    options={[
                                        { value: 'active', label: 'Active' },
                                        { value: 'inactive', label: 'Inactive' },
                                    ]}
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'employment'} />
                            </Section>

                            {/* Emergency Contact */}
                            <Section
                                title="Emergency Contact" icon={FiAlertCircle} section="emergency"
                                editingSection={editingSection} saving={saving} canEdit={canEdit}
                                onStartEdit={() => startEditing('emergency')}
                                onCancel={cancelEditing} onSave={saveEdit}
                            >
                                <Field label="Contact Name" value={user.emergency_contact_name} field="emergency_contact_name"
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'emergency'} />
                                <Field label="Contact Phone" value={user.emergency_contact_phone} field="emergency_contact_phone" type="tel"
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'emergency'} />
                            </Section>

                            {/* Vehicle (drivers only) */}
                            {user.role === 'driver' && (
                                <Section
                                    title="Vehicle & License" icon={FiTruck} section="vehicle"
                                    editingSection={editingSection} saving={saving} canEdit={canEdit}
                                    onStartEdit={() => startEditing('vehicle')}
                                    onCancel={cancelEditing} onSave={saveEdit}
                                >
                                    <Field label="License No." value={user.license_number} field="license_number"
                                        editData={editData} setEditData={setEditData} isEditing={editingSection === 'vehicle'} />
                                    <Field label="License Expiry" value={fmt(user.license_expiry)} field="license_expiry" type="date"
                                        editData={editData} setEditData={setEditData} isEditing={editingSection === 'vehicle'} />
                                    <Field label="Vehicle Type" value={user.vehicle_type} field="vehicle_type"
                                        editData={editData} setEditData={setEditData} isEditing={editingSection === 'vehicle'} />
                                    <Field label="Registration" value={user.vehicle_registration} field="vehicle_registration"
                                        editData={editData} setEditData={setEditData} isEditing={editingSection === 'vehicle'} />
                                </Section>
                            )}

                            {/* Preferences */}
                            <Section
                                title="Preferences" icon={FiSettings} section="preferences"
                                editingSection={editingSection} saving={saving} canEdit={canEdit}
                                onStartEdit={() => startEditing('preferences')}
                                onCancel={cancelEditing} onSave={saveEdit}
                            >
                                <Field label="Theme" value={user.theme_preference || 'System'} field="theme_preference" type="select"
                                    options={[
                                        { value: 'light', label: 'Light' },
                                        { value: 'dark', label: 'Dark' },
                                        { value: 'system', label: 'System' },
                                    ]}
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'preferences'} />
                                <Field label="Language" value={user.language || 'English'} field="language" type="select"
                                    options={[
                                        { value: 'en', label: 'English' },
                                        { value: 'es', label: 'Spanish' },
                                        { value: 'fr', label: 'French' },
                                    ]}
                                    editData={editData} setEditData={setEditData} isEditing={editingSection === 'preferences'} />
                            </Section>
                        </DropdownSection>

                        {showPermissionSection && (
                            <DropdownSection
                                title="Notifications & Preferences"
                                titleStyle="text-zinc-700 text-sm"
                                icon={MdOutlineEditNotifications}
                                iconStyle="text-zinc-800 !w-4 !h-4 self-center"
                                subtext={``}
                                subtextStyle="text-xs "
                                defaultOpen={false}
                            >
                                <div className="space-y-3">
                                    {PERMISSION_GROUPS.map((group) => {
                                        const canManageAnyPermissions = canModifyUserPermissions || canModifyUserRoles;

                                        const visiblePermissions = group.permissions.filter((permission) => {
                                            if (permission.key === 'MODIFY_USER_ROLES') {
                                                return canModifyUserRoles;
                                            }

                                            return canManageAnyPermissions;
                                        });

                                        if (visiblePermissions.length === 0) return null;

                                        return (
                                            <div
                                                key={group.title}
                                                className="border border-gray-200 rounded-xl overflow-hidden bg-white"
                                            >
                                                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                                    <p className="text-sm font-bold text-gray-800">
                                                        {group.title}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {group.description}
                                                    </p>
                                                </div>

                                                <div className="p-2 space-y-2">
                                                    {visiblePermissions.map((permission) => {
                                                        const enabled = (user.permissions || []).includes(permission.key);

                                                        const disabled =
                                                            savingPermission === permission.key ||
                                                            (
                                                                permission.key === 'MODIFY_USER_ROLES' &&
                                                                user.role === 'super_admin'
                                                            ) ||
                                                            (
                                                                permission.key === 'MODIFY_USER_ROLES' &&
                                                                user.id === userProfile?.id &&
                                                                enabled
                                                            );

                                                        return (
                                                            <PermissionToggle
                                                                key={permission.key}
                                                                permission={permission}
                                                                enabled={enabled}
                                                                disabled={disabled}
                                                                saving={savingPermission === permission.key}
                                                                onToggle={() => handlePermissionToggle(permission.key)}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </DropdownSection>
                        )}

                        <DropdownSection
                            title="Account Info"
                            titleStyle="text-zinc-700 text-sm"
                            icon={FiClock}
                            iconStyle="text-zinc-800 !w-4 !h-4 self-center"
                            subtext={``}
                            subtextStyle="text-xs "
                            defaultOpen={false}
                        >
                            {/* Account timestamps */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 p-0">
                                <MetaChip label="Created" value={fmtDT(user.created_at)} />
                                <MetaChip label="Last Updated" value={fmtDT(user.updated_at)} />
                                <MetaChip label="Last Login" value={fmtDT(user.last_login_at)} />
                                <MetaChip label="Last Active" value={fmtDT(user.last_active_at)} />
                            </div>
                        </DropdownSection>

                        {/* Logout Button */}
                        <button
                            onClick={handleLogout}
                            className={`flex items-center rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all h-10 w-full sm:w-32 sm:ml-auto justify-center text-center'
                                }`}
                            title={!'Sign Out'}
                        >
                            <FiLogOut className="w-5 h-5 flex-shrink-0 mr-2" />
                            <span className="font-medium text-sm whitespace-nowrap">Sign Out</span>
                        </button>


                        {/* Danger zone */}
                        {canDeleteOwnAccount && (
                            <DropdownSection
                                title="Danger Zone"
                                titleStyle="text-red-800 text-sm"
                                icon={MdWarning}
                                iconStyle="text-red-800 !w-4 !h-4 self-center"
                                subtext={``}
                                subtextStyle="text-xs "
                                defaultOpen={false}
                                customContainerStyle=""
                                customMainStyle="border-red-200"
                                customClickableStyle="bg-red-50 hover:bg-red-100"
                            >
                                <div className="">
                                    <div className="flex flex-col sm:items-start sm:justify-between px-2 py-2 gap-3">
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">Delete account</p>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                This will remove your login access and personal profile details. Operational records may remain for audit history.
                                            </p>
                                        </div>

                                        <button
                                            onClick={() => {
                                                setDeleteStep(1);
                                                setDeleteText('');
                                                setDeleteCode('');
                                                setShowAccountDeletePopup(true);
                                            }}
                                            className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors w-full sm:w-auto"
                                        >
                                            <FiTrash2 className="w-4 h-4" />
                                            Delete Account
                                        </button>
                                    </div>
                                </div>
                            </DropdownSection>
                        )}
                    </>
                ) : null}
            </div>
        </div >
    );

    // ── Modal or page ─────────────────────────────────────────────────────────

    const inner = (
        <>
            {content}
            <ConfirmationPopup
                isOpen={showDeleteConfirm}
                title="Delete User"
                message={`Are you sure you want to delete ${user?.username}? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteUser}
                onCancel={() => setShowDeleteConfirm(false)}
            />
            {showAccountDeletePopup && (
                <div className="mobile-dialog-shell fixed inset-0 z-[10000] flex items-end sm:items-center justify-center px-3 sm:px-4 py-3 sm:py-0">
                    <div className="absolute inset-0 bg-black/40" onClick={() => !deleteLoading && setShowAccountDeletePopup(false)} />

                    <div className="relative w-full max-w-md max-h-[92vh] overflow-y-auto bg-white rounded-2xl sm:rounded-2xl shadow-2xl border border-gray-200">
                        <div className="px-5 py-4 border-b border-gray-200 bg-red-50">
                            <h3 className="text-sm font-bold text-red-800">Delete Account</h3>
                            <p className="text-xs text-red-700 mt-1">
                                Step {deleteStep} of 2
                            </p>
                        </div>

                        <div className="p-4 sm:p-5 space-y-4">
                            {deleteStep === 1 ? (
                                <>
                                    <p className="text-sm text-gray-700 leading-6">
                                        Deleting your account will <b>permanently remove</b> your login access and personal profile information.
                                        Your username and user ID may remain for company audit records.
                                    </p>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                            Type DELETE to continue
                                        </label>
                                        <input
                                            value={deleteText}
                                            onChange={(e) => setDeleteText(e.target.value)}
                                            placeholder="DELETE"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                        />
                                    </div>

                                    <button
                                        disabled={deleteText !== 'DELETE' || deleteLoading}
                                        onClick={handleRequestDeletionCode}
                                        className="w-full h-10 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white text-sm font-bold transition-colors"
                                    >
                                        {deleteLoading ? 'Sending code…' : 'Send verification code'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-700 leading-6">
                                        We sent a 6-digit verification code to your email address. Enter it below to permanently delete your account.
                                    </p>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                            Verification code
                                        </label>
                                        <input
                                            value={deleteCode}
                                            onChange={(e) => setDeleteCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="123456"
                                            inputMode="numeric"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm tracking-[0.3em] text-center font-bold focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                        />
                                    </div>

                                    <button
                                        disabled={deleteCode.length !== 6 || deleteLoading}
                                        onClick={handleConfirmAccountDeletion}
                                        className="w-full h-10 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white text-sm font-bold transition-colors"
                                    >
                                        {deleteLoading ? 'Deleting account…' : 'Delete my account'}
                                    </button>
                                </>
                            )}

                            <button
                                disabled={deleteLoading}
                                onClick={() => setShowAccountDeletePopup(false)}
                                className="w-full !mt-2 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-60 text-gray-700 text-sm font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    if (isModal) {
        return (
            <div className="mobile-dialog-shell fixed inset-0 top-0 right-0 !mt-0 z-[9999] flex justify-end sm:py-4 sm:px-4">
                <div className="absolute inset-0 bg-black/40" onClick={onClose} />
                <div className="relative rounded-none sm:rounded-xl w-full max-w-full sm:max-w-lg h-full overflow-hidden bg-white shadow-2xl flex flex-col">
                    {inner}
                </div>
            </div>
        );
    }

    return <div className="mobile-detail-page user-detail-page">{inner}</div>;
};

export default UserDetails;
